import { connect, useSelector } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  ICondosStaticState,
  IWeekState,
  weeksActions,
  weeksLocationsActions,
} from '@share/store/slices';
import { ILoginState } from '@share/store/slices';

import './style.scss';
import { FunctionalRootState, RootState, scrollTop } from '@share/utils';
import { InsuranceSearch } from '../insurance-search-wrapper';
import LifePreserverImg from '@assets/images/insurance/life-preserver.svg';
import ShieldCheckImg from '@assets/images/insurance/shield-check.svg';
import GearWideImg from '@assets/images/insurance/gear-wide-connected.svg';
import { FormattedMessage } from 'react-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import { isEmpty } from 'lodash';
import { IQuoteCheckoutResponse } from '../../../../store/slices/insurance-quote';
import GlobalSpinner from '../global-spinner/component';
import { INSURANCE_AVAILABLE_STATES } from '@constants';

interface IMapStateToProps {
  weeksStore: IWeekState;
  loginStore: ILoginState;
  condosStaticStore: ICondosStaticState;
}

interface IMapDispatchToProps {
  setDefaultValues: () => void;
  resetLocations: () => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {}


export interface InsuranceFilter {
  destination: string | null;
  travelDates: [Moment | null, Moment | null] | null;
  travelers: number | null;
  state: string | null;
  tripCost: number | null;
}

const PREFILL_DATE_FORMAT = 'yyyy-MM-DD';
const MIN_TRAVELERS = 1;
const MAX_TRAVELERS = 50;

/**
 * The insurance cross-sell banner and the reminder email link here with the booking data
 * already resolved. Every parameter is optional and dropped when it does not validate, so
 * the form still renders as a usable, fully editable partially-filled form.
 */
const getPrefillDates = (search: URLSearchParams): [Moment, Moment] | null => {
  const from = search.get('from');
  const to = search.get('to');

  if (!from || !to) {
    return null;
  }

  const fromDate = moment(from, PREFILL_DATE_FORMAT, true);
  const toDate = moment(to, PREFILL_DATE_FORMAT, true);

  if (!fromDate.isValid() || !toDate.isValid() || !toDate.isAfter(fromDate)) {
    return null;
  }

  return [fromDate, toDate];
};

const getPrefillTravelers = (search: URLSearchParams): number | null => {
  const travelers = Number(search.get('travelers'));

  return Number.isInteger(travelers) && travelers >= MIN_TRAVELERS && travelers <= MAX_TRAVELERS
    ? travelers
    : null;
};

/** Matches a country by ISO code or by name, so both "US" and "USA" resolve. */
const findCountryCode = (value?: string | null, countries?: any[]): string | null => {
  const country = value?.trim().toUpperCase();

  if (!country || !countries?.length) {
    return null;
  }

  return (
    countries.find(
      (c: any) =>
        c?.isoCode?.toUpperCase() === country || c?.name?.toUpperCase() === country,
    )?.isoCode ?? null
  );
};

const getPrefillTripCost = (search: URLSearchParams): number | null => {
  const tripCost = Number(search.get('tripCost'));

  return Number.isFinite(tripCost) && tripCost > 0 ? tripCost : null;
};

const InsuranceResultsPageComponent = (props: IProps) => {
  const { condosCountriesCode } = props.condosStaticStore;

  const prefill = useMemo(
    () => new URLSearchParams(props.location.search),
    [props.location.search],
  );

  /* A link that names a booking is quoted from that booking alone. Every other parameter is
     editable in the address bar and every one of them moves the premium, so they are ignored
     outright rather than merged. The form starts empty and fills once the booking answers. */
  const hasBooking = !isEmpty(prefill.get('bookingId'));

  const [insuranceFilter, setInsuranceFilter] = useState<InsuranceFilter>({
    destination: null,
    travelDates: hasBooking ? null : getPrefillDates(prefill),
    travelers: hasBooking ? null : getPrefillTravelers(prefill),
    state: null,
    tripCost: hasBooking ? null : getPrefillTripCost(prefill),
  });

  const states = useMemo(
    () => condosCountriesCode?.rciCountries?.find((c) => c.isoCode === 'US')?.states ?? [],
    [condosCountriesCode, insuranceFilter],
  );

  const filteredStates = states
    ? useMemo(() => states.filter((s: any) => INSURANCE_AVAILABLE_STATES.includes(s.code)), [states])
    : [];

  /* Reached from the cross-sell banner and the reminder email, so start at the top of the
     form rather than wherever the previous page was scrolled to. */
  useEffect(() => {
    scrollTop();
  }, []);

  // condosCountriesCode arrives asynchronously, so destination and state can only be
  // validated once it resolves. Seeded a single time to never override the user's edits.
  const isPrefilled = useRef<boolean>(false);

  useEffect(() => {
    if (isPrefilled.current || !condosCountriesCode?.rciCountries?.length) {
      return;
    }

    isPrefilled.current = true;

    // A booking supplies its own destination and state; this path is the plain link.
    const destination = hasBooking
      ? undefined
      : findCountryCode(prefill.get('country'), condosCountriesCode?.rciCountries) ?? undefined;

    const stateCode = hasBooking ? undefined : prefill.get('state')?.trim().toUpperCase();
    const state = filteredStates?.find((s: any) => s.code?.toUpperCase() === stateCode)?.code;

    if (!destination && !state) {
      return;
    }

    setInsuranceFilter((prevState: InsuranceFilter) => ({
      ...prevState,
      destination: destination ?? prevState.destination,
      state: state ?? prevState.state,
    }));
  }, [condosCountriesCode, filteredStates, prefill, hasBooking]);

  const insuranceQuoteStore: IQuoteCheckoutResponse = useSelector(
    (state: FunctionalRootState) => state.insuranceQuoteStore,
  );

  const { bookingQuoteFilter } = insuranceQuoteStore;
  const isBookingSeeded = useRef<boolean>(false);

  useEffect(() => {
    // Waits on the states list so the residency state is not dropped on the way in.
    if (isBookingSeeded.current || !hasBooking || !bookingQuoteFilter || !filteredStates?.length) {
      return;
    }

    isBookingSeeded.current = true;

    const { checkIn, checkOut, travelers, tripCost, state, destination } = bookingQuoteFilter;

    const checkInDate = checkIn ? moment(checkIn, PREFILL_DATE_FORMAT, true) : null;
    const checkOutDate = checkOut ? moment(checkOut, PREFILL_DATE_FORMAT, true) : null;
    const travelDates =
      checkInDate?.isValid() && checkOutDate?.isValid() && checkOutDate.isAfter(checkInDate)
        ? ([checkInDate, checkOutDate] as [Moment, Moment])
        : null;

    setInsuranceFilter((prevState: InsuranceFilter) => ({
      ...prevState,
      travelDates: travelDates ?? prevState.travelDates,
      travelers: travelers ?? prevState.travelers,
      tripCost: tripCost ?? prevState.tripCost,
      state: filteredStates?.find((s: any) => s.code?.toUpperCase() === state)?.code
        ?? prevState.state,
      destination:
        findCountryCode(destination, condosCountriesCode?.rciCountries) ?? prevState.destination,
    }));
  }, [hasBooking, bookingQuoteFilter, filteredStates, condosCountriesCode]);

  return (
    <section className="insurance__page">
      <header className="insurance__header">
        <section className="insurance__header-background">
          <h2>
            <FormattedMessage id="insurance.header.title" />
          </h2>
          <p>
            <FormattedMessage id="insurance.header.subtitle" />
          </p>
        </section>
        <InsuranceSearch
          setInsuranceFilter={setInsuranceFilter}
          insuranceFilter={insuranceFilter}
          states={filteredStates}
          insuranceQuoteStore={insuranceQuoteStore}
          condosCountriesCode={condosCountriesCode}
        />
      </header>

      <section className="insurance__travel">
        <section className="insurance__travel-container">
          <section className="insurance__travel-container-one">
            <h2>
              <FormattedMessage id="insurance.travel.title" />
            </h2>
          </section>
          <section className="insurance__travel-container-two">
            <h3 className="insurance__travel-container-two-title">
              <FormattedMessage id="insurance.travel.subtitle" />
            </h3>
            <section className="insurance__travel-container-two-paragraph">
              <h5>
                <FormattedMessage id="insurance.travel.ptitle1" />
              </h5>
              <p>
                <FormattedMessage id="insurance.travel.paragraph1" />
              </p>
            </section>
            <section className="insurance__travel-container-two-paragraph">
              <h5>
                <FormattedMessage id="insurance.travel.ptitle2" />
              </h5>
              <p>
                <FormattedMessage id="insurance.travel.paragraph2" />
              </p>
            </section>
            <section className="insurance__travel-container-two-paragraph">
              <h5>
                <FormattedMessage id="insurance.travel.ptitle3" />
              </h5>
              <p>
                <FormattedMessage id="insurance.travel.paragraph3" />
              </p>
            </section>
            <section className="insurance__travel-container-two-paragraph">
              <h5>
                <FormattedMessage id="insurance.travel.ptitle4" />
              </h5>
              <p>
                <FormattedMessage id="insurance.travel.paragraph4" />
              </p>
            </section>
            <section className="insurance__travel-container-two-paragraph">
              <h5>
                <FormattedMessage id="insurance.travel.ptitle5" />
              </h5>
              <p>
                <FormattedMessage id="insurance.travel.paragraph5" />
              </p>
            </section>
          </section>
        </section>
      </section>

      <section className="insurance__protection">
        <section className="insurance__protection-container">
          <section className="insurance__protection-container-text">
            <h2>
              <FormattedMessage id="insurance.protection.title1" />{' '}
              <span>
                {' '}
                <FormattedMessage id="insurance.protection.title2" />
              </span>
            </h2>
            <p>
              <FormattedMessage id="insurance.protection.subtitle" />
            </p>
          </section>
          <section className="insurance__protection-container-plans">
            <section className="insurance__protection-container-plans-one">
              <img
                src={ShieldCheckImg}
                alt="Shield Check Img"
                className="insurance__protection-container-plans-one-icons"
              />
              <h3>
                <FormattedMessage id="insurance.protection.ptitle1" />
              </h3>
              <p>
                <FormattedMessage id="insurance.protection.paragraph1" />
              </p>
            </section>
            <section className="insurance__protection-container-plans-two">
              <img
                src={LifePreserverImg}
                alt="Life preserver Img"
                className="insurance__protection-container-plans-one-icons"
              />
              <h3>
                {' '}
                <FormattedMessage id="insurance.protection.ptitle2" />
              </h3>
              <p>
                <FormattedMessage id="insurance.protection.paragraph2" />
              </p>
            </section>
            <section className="insurance__protection-container-plans-three">
              <img
                src={GearWideImg}
                alt="Gear Wide Img"
                className="insurance__protection-container-plans-one-icons"
              />
              <h3>
                {' '}
                <FormattedMessage id="insurance.protection.ptitle3" />
              </h3>
              <p>
                <FormattedMessage id="insurance.protection.paragraph3" />
              </p>
            </section>
          </section>
          {/* <button>View Our Plans</button> */}
        </section>
      </section>

      <GlobalSpinner spinning={insuranceQuoteStore.quote.loading} />
    </section>
  );
};

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    weeksStore: state.weeksStore,
    loginStore: state.loginStore,
    condosStaticStore: state.condosStaticStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setDefaultValues: weeksActions.setDefaultValues,
  resetLocations: weeksLocationsActions.resetWeeksLocations,
};

export const InsuranceResultsPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(InsuranceResultsPageComponent));
